import React, { FormEvent, useEffect, useState } from 'react';
import { Button } from '@mui/material';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import Appbar from 'components/appbar/Appbar';
import AddressFields from 'components/address/AddressFields';
import InsideSaving from 'components/loading/InsideSaving';
import {
  ContentPad,
  PageShell,
  RecordFooter,
  RecordHeader,
  StyledForm,
  WorkArea,
} from 'components/record-shell/RecordShell';
import { AddressErrors, emptyAddress, isAddressFilled, validateAddress } from 'helpers/addressValidation';
import { api } from 'services/api';
import history from 'services/history';
import { Address } from 'types/address';
import { Bank } from 'types/bank';
import { Hospital, HospitalBank, HospitalProcedure } from 'types/hospital';
import HospitalBanks from '../HospitalBanks';
import HospitalProcedures from '../HospitalProcedures';
import HospitalForm from '../form/HospitalForm';
import HospitalTab from '../tab/HospitalTab';
import { useHospitalValidation } from '../validation/useHospitalValidation';

const NewHospital: React.FC = () => {
  const [validation, setValidation, validate] = useHospitalValidation();
  const [tab, setTab] = useState('main');
  const [saving, setSaving] = useState(false);
  const [addressErrors, setAddressErrors] = useState<AddressErrors>({});
  const [banks, setBanks] = useState<Bank[]>([]);
  const [hospitalAddress, setHospitalAddress] = useState<Address>(emptyAddress);
  const [hospital, setHospital] = useState<Hospital>({
    id: 0,
    company_name: '',
    company_trade_name: '',
    active: true,
    banks: [],
    procedures: [],
    address: null,
    document_number: '',
    email: '',
    fixed_phone: '',
    observation: '',
    phone: '',
    sector: '',
  });

  useEffect(() => {
    fetch('https://brasilapi.com.br/api/banks/v1')
      .then(response => response.json())
      .then(data => setBanks(data.filter((item: Bank) => !!item.code)))
      .catch(error => console.error('Error fetching bank data:', error));
  }, []);

  // a validação guarda o erro do campo; abre a aba onde ele está
  useEffect(() => {
    if (Object.keys(validation).length > 0) setTab('main');
  }, [validation]);

  function goBack() {
    history.push('/hospitals');
  }

  function handleChange(index: keyof Hospital, value: any) {
    setHospital(prev => ({ ...prev, [index]: value }));
  }

  function handleAddressChange(address: Address) {
    setHospitalAddress(address);
    setAddressErrors({});
  }

  function handleSubmit() {
    setSaving(true);

    api
      .post('/hospitals', { ...hospital, address: isAddressFilled(hospitalAddress) ? hospitalAddress : null })
      .then(response => {
        toast.success('Hospital cadastrado');
        // abre o hospital recém-cadastrado, na ficha
        history.push(response.data?.id ? `/hospitals/${response.data.id}` : '/hospitals');
      })
      .catch(err => {
        toast.error(err.response?.data?.error || 'Não foi possível cadastrar o hospital');
      })
      .finally(() => setSaving(false));
  }

  function handleValidation(e?: FormEvent<HTMLFormElement>) {
    e?.preventDefault();

    validate(hospital)
      .then(() => {
        const errors = validateAddress(hospitalAddress);
        setAddressErrors(errors);

        if (Object.keys(errors).length > 0) {
          setTab('address');
          toast.error('Corrija o endereço para salvar');
          return;
        }

        handleSubmit();
        setValidation({});
      })
      .catch((err: yup.ValidationError) => {
        if (err instanceof yup.ValidationError) {
          setValidation({
            [err.path as string]: err.message,
          });
        }
      });
  }

  // procedimentos e contas ficam na memória até o hospital ser salvo
  function handleChangeAddBank(bank: HospitalBank) {
    setHospital(prev => {
      const existingBank = prev.banks.find(b => b.id === bank.id);
      if (existingBank) {
        return {
          ...prev,
          banks: prev.banks.map(b => (b.id === bank.id ? { ...b, ...bank } : b)),
        };
      }
      return {
        ...prev,
        banks: [...prev.banks, { ...bank, id: prev.banks.length ? Math.max(...prev.banks.map(b => b.id)) + 1 : 1 }],
      };
    });
  }

  function handleDeleteBank(bank: HospitalBank) {
    setHospital(prev => ({
      ...prev,
      banks: prev.banks.filter(b => b.id !== bank.id),
    }));
  }

  function handleChangeAddProcedure(procedure: HospitalProcedure) {
    setHospital(prev => {
      const existingProcedure = prev.procedures.find(b => b.id === procedure.id);
      if (existingProcedure) {
        return {
          ...prev,
          procedures: prev.procedures.map(b => (b.id === procedure.id ? { ...b, ...procedure } : b)),
        };
      }
      return {
        ...prev,
        procedures: [
          ...prev.procedures,
          { ...procedure, id: prev.procedures.length ? Math.max(...prev.procedures.map(b => b.id)) + 1 : 1 },
        ],
      };
    });
  }

  function handleDeleteProcedure(procedure: HospitalProcedure) {
    setHospital(prev => ({
      ...prev,
      procedures: prev.procedures.filter(b => b.id !== procedure.id),
    }));
  }

  return (
    <>
      <Appbar title="Hospitais" backAction={goBack} />

      {saving && <InsideSaving />}

      <PageShell>
        <RecordHeader title="Novo hospital" backAction={goBack} />

        <WorkArea>
          <HospitalTab
            onChange={newTab => setTab(newTab)}
            tab={tab}
            proceduresCount={hospital.procedures.length}
            banksCount={hospital.banks.length}
          />
          <ContentPad>
            {tab === 'procedures' ? (
              <HospitalProcedures
                hospital={hospital}
                handleDeleteProcedure={handleDeleteProcedure}
                handleChangeAddProcedure={handleChangeAddProcedure}
              />
            ) : tab === 'banks' ? (
              <HospitalBanks
                handleDeleteBank={handleDeleteBank}
                banks={banks}
                hospital={hospital}
                handleChangeAddBank={handleChangeAddBank}
              />
            ) : (
              <StyledForm onSubmit={handleValidation}>
                {tab === 'address' ? (
                  <AddressFields address={hospitalAddress} onChange={handleAddressChange} errors={addressErrors} />
                ) : (
                  <HospitalForm handleChange={handleChange} hospital={hospital} validation={validation} />
                )}
                <button type="submit" style={{ display: 'none' }}></button>
              </StyledForm>
            )}
          </ContentPad>
        </WorkArea>

        <RecordFooter>
          <Button variant="outlined" color="inherit" size="small" disabled={saving} onClick={goBack}>
            Cancelar
          </Button>
          <Button variant="contained" size="small" disabled={saving} onClick={() => handleValidation()}>
            Salvar hospital
          </Button>
        </RecordFooter>
      </PageShell>
    </>
  );
};

export default NewHospital;
